import { calculateRoomNightGstEstimate, applyInclusiveNetSplit } from './gstSlabCalculation';
import { resolveReviewDiscount, formatDiscountLabel } from './reviewDiscountUtils';

export const roundBookingCurrency = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

/**
 * Preview / review-step pricing: room lines + discount + GST (inclusive or exclusive).
 * Single source of truth for quick booking, walk-in, and reservation review totals.
 */
export function calculateBookingPriceBreakdown({
  roomLines = [],
  taxRules = [],
  gstIncluded = false,
  taxInclusive = false,
  allowDiscount = false,
  discountMode = 'percentage',
  discountPercentage = 0,
  discountAmount = 0,
  allowExtraCharges = false,
  extraChargesAmount = 0,
  extraChargesNote = '',
  totalBasePrice,
  totalRoomBasePrice = 0,
  totalExtraAdultCharge = 0,
  totalExtraChildCharge = 0,
  gstPercentage = 0,
  hasTaxRules = false,
}) {
  const base =
    totalBasePrice != null
      ? Number(totalBasePrice) || 0
      : roomLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

  const resolvedDiscount = resolveReviewDiscount({
    allowDiscount,
    discountMode,
    discountPercentage,
    discountAmount,
    totalBasePrice: base,
  });
  const totalDiscount = resolvedDiscount.totalDiscount;
  const priceAfterDiscount = base - totalDiscount;
  const gstActive = Boolean(gstIncluded && hasTaxRules);
  const applyInclusive = Boolean(taxInclusive && gstActive);

  const gstEstimate = calculateRoomNightGstEstimate({
    roomLines,
    taxRules,
    gstIncluded: gstActive,
    taxInclusive: applyInclusive,
    totalDiscount: allowDiscount ? totalDiscount : 0,
  });
  const totalGST = gstEstimate.totalGST;
  const serviceCharges = allowExtraCharges ? roundBookingCurrency(extraChargesAmount) : 0;
  const roomTotal = applyInclusive ? priceAfterDiscount : priceAfterDiscount + totalGST;
  const total = roundBookingCurrency(roomTotal + serviceCharges);
  const netAfterDiscount = applyInclusive ? gstEstimate.netTaxableTotal : priceAfterDiscount;
  const netSplit = applyInclusiveNetSplit(
    {
      roomBasePrice: totalRoomBasePrice,
      totalExtraAdultCharge,
      totalExtraChildCharge,
      priceBeforeDiscount: base,
    },
    priceAfterDiscount,
    netAfterDiscount,
    applyInclusive
  );

  return {
    roomBasePrice: totalRoomBasePrice,
    totalExtraAdultCharge,
    totalExtraChildCharge,
    priceBeforeDiscount: base,
    totalDiscount: allowDiscount ? totalDiscount : 0,
    discountMode: allowDiscount ? discountMode : 'percentage',
    discountDisplayLabel: allowDiscount
      ? formatDiscountLabel({ discountMode, discountPercentage, discountAmount })
      : '',
    priceAfterDiscount,
    totalGST,
    extraChargesAmount: serviceCharges,
    extraChargesNote: allowExtraCharges ? (extraChargesNote || '').trim() : '',
    total,
    netAfterDiscount,
    taxInclusive: applyInclusive,
    ...netSplit,
    gstPercentage: gstEstimate.gstPercentage || gstPercentage,
    gstLineBreakdown: gstEstimate.lineBreakdown || [],
  };
}

/**
 * Normalize persisted booking / payment_summary amounts for consistent UI labels.
 * Matches backend calculate_booking_totals + get_booking_calculated_breakdown.
 */
export function inferBookingTaxInclusive({
  gstIncluded = false,
  taxInclusive = false,
  subtotalBeforeDiscount = 0,
  discountAmount = 0,
  totalAmount = 0,
  extraChargesAmount = 0,
}) {
  if (!gstIncluded) return false;
  if (taxInclusive) return true;
  const grossAfterDiscount = roundBookingCurrency(
    Math.max(0, Number(subtotalBeforeDiscount) - Number(discountAmount))
  );
  const extra = roundBookingCurrency(extraChargesAmount);
  const roomTotal = roundBookingCurrency(Math.max(0, Number(totalAmount) - extra));
  if (roomTotal > 0 && Math.abs(roomTotal - grossAfterDiscount) <= 0.05) {
    return true;
  }
  return false;
}

export function resolveBookingBreakdownDisplay({
  subtotalBeforeDiscount = 0,
  discountAmount = 0,
  subtotalAfterDiscount = 0,
  gstAmount = 0,
  gstPercentage = 0,
  gstIncluded = false,
  taxInclusive = false,
  totalAmount = 0,
  discountPercentage = 0,
  paidAmount = 0,
  remainingAmount = 0,
  currency = 'INR',
  extraChargesAmount = 0,
  extraChargesBaseAmount = 0,
  extraChargesGstAmount = 0,
  extraChargesNote = '',
  extraChargesItems = [],
}) {
  const grossSubtotal = roundBookingCurrency(subtotalBeforeDiscount);
  const discount = roundBookingCurrency(discountAmount);
  const afterDiscountStored = roundBookingCurrency(subtotalAfterDiscount);
  const gst = roundBookingCurrency(gstAmount);
  const gstPct = Number(gstPercentage) || 0;
  const gstOn = Boolean(gstIncluded);
  const inclusiveDisplay = gstOn && Boolean(taxInclusive);

  const roomGrossAfterDiscount = roundBookingCurrency(afterDiscountStored + gst);
  // Inclusive: subtotal_before_discount is the GST-inclusive gross; after_discount is taxable net.
  // Do not reconstruct subtotal as taxable + discount (that mislabels GST as discount).
  const displaySubtotal = grossSubtotal;
  const displayAfterDiscount = afterDiscountStored;
  const afterDiscountLabel = inclusiveDisplay ? 'Taxable Amount' : 'After Discount';

  const exclusiveTotal = roundBookingCurrency(afterDiscountStored + gst);
  const extraCharges = roundBookingCurrency(extraChargesAmount);
  const extraChargesBase = roundBookingCurrency(extraChargesBaseAmount || extraChargesAmount);
  const extraChargesGst = roundBookingCurrency(extraChargesGstAmount);
  const displayTotal = roundBookingCurrency(
    totalAmount > 0
      ? totalAmount
      : (inclusiveDisplay ? roomGrossAfterDiscount : exclusiveTotal) + extraCharges
  );
  const paid = roundBookingCurrency(paidAmount);
  const returnAmount = roundBookingCurrency(Math.max(0, paid - displayTotal));
  const normalizedRemaining = roundBookingCurrency(Math.max(0, displayTotal - paid));

  return {
    subtotalBeforeDiscount: grossSubtotal,
    discountAmount: discount,
    discountPercentage,
    subtotalAfterDiscount: afterDiscountStored,
    gstAmount: gst,
    gstPercentage: gstPct,
    gstIncluded: gstOn,
    taxInclusive: inclusiveDisplay,
    totalAmount: displayTotal,
    paidAmount: paid,
    remainingAmount: normalizedRemaining,
    returnAmount,
    currency,
    inclusiveDisplay,
    displaySubtotal,
    displayAfterDiscount,
    afterDiscountLabel,
    gstPrefix: inclusiveDisplay ? '+ ' : '',
    displayGstAmount: gst,
    extraChargesAmount: extraCharges,
    extraChargesBaseAmount: extraChargesBase,
    extraChargesGstAmount: extraChargesGst,
    extraChargesNote: (extraChargesNote || '').trim(),
    extraChargesItems: Array.isArray(extraChargesItems) ? extraChargesItems : [],
    displayTotal,
  };
}
